import {flatten, isNil} from "lodash";
import moment from "moment";
import {Level} from "../constans/levelTypes";
import {
    getFirstTechnologyGroupStart,
    getTechnologyGroupWeeks,
} from "./SettingsUtils";
import {
    REMOVE_TECHNOLOGY_GROUP_REASONS,
    formatTechnoGroupName,
    getLastTechnologyGroupNames,
    getTechnologyGroupByTime,
    getTechnologyGroupStartByName,
    getTechnologyGroupWeeksByYear,
} from "./TechnologyGroupUtils";
import {createKeyToIndexDictionary, createKeyToValueDictionary} from "./Utils";

function createBaseQuery(field, row) {
    if (!row) return "";
    const key = Object.keys(row)[0];
    if (!key || isNil(row[key])) return "";
    if (!keyFunctions[key]) {
        console.warn("NOT FOUND FUNCTION FOR", key);
        return "";
    }
    return keyFunctions[key](field, row);
}

function createGreaterThanOrEqualsQuery(field, row) {
    return `${field} >= ${row.$sanGte}`;
}

function createEqualsQuery(field, row) {
    if (typeof row.$eq === "string") return `${field} = '${row.$eq}'`;
    if (typeof row.$sanEq === "string") return `${field} = '${row.$sanEq}'`;
    return `${field} = ${row.$sanEq ?? row.$eq}`;
}

function createLessThanOrEqualsQuery(field, row) {
    return `${field} <= ${row.$sanLte}`;
}

function createNotEqualsQuery(field, row) {
    if (typeof row.$sanNe === "string") return `${field} <> '${row.$sanNe}'`;
    return `${field} <> ${row.$sanNe}`;
}

function createStringIncludesQuery(field, row) {
    return `${field} LIKE '%${row.$includes}%'`;
}

function createStartsWithQuery(field, row) {
    return `${field} LIKE '${row.$startsWith}%'`;
}

function createEndsWithQuery(field, row) {
    return `${field} LIKE '%${row.$endsWith}'`;
}

function createDateEqualsQuery(field, row) {
    const endOfDay = moment(row.$dateEq).endOf("day");
    return `(${field} >= ${+new Date(
        row.$dateEq
    )} AND ${field} <= ${+endOfDay})`;
}

function createDateGreaterThanOrEqualsQuery(field, row) {
    // tutaj nie ma znaczenia, bo data to poczatek dnia
    return `${field} >= ${+new Date(row.$dateGte)}`;
}

function createDateLessThanOrEqualsQuery(field, row) {
    // mamy eventy dociagane do dnia, wiec data jest tworzona na koniec dnia
    const date = moment(row.$dateLte).endOf("day");
    return `${field} <= ${+date}`;
}

function createTechnologyGroupAgeQuery(field, row) {
    const time = moment.utc().subtract(row.$techGroupAge, "weeks");
    const group = getTechnologyGroupByTime(+time);
    return `group_nr = '${formatTechnoGroupName(
        group.start,
        group.end,
        true
    )}'`;
}

function createYesterdayQuery(field, row) {
    const yesterday = moment().subtract(1, "days").startOf("day");
    return createDateEqualsQuery(field, {$dateEq: yesterday});
}

function createWeekQuery(field, subtract = 0) {
    const start = moment().subtract(subtract, "weeks").startOf("week");
    const end = moment().subtract(subtract, "weeks").endOf("week");
    return `${field} >= ${+start} AND ${field} <= ${+end}`;
}

function createThisWeekQuery(field, row) {
    return createWeekQuery(field, 0);
}

function createLastWeekQuery(field, row) {
    return createWeekQuery(field, 1);
}

function createMonthQuery(field, subtract = 0) {
    const start = moment().subtract(subtract, "months").startOf("month");
    const end = moment().subtract(subtract, "months").endOf("month");
    return `${field} >= ${+start} AND ${field} <= ${+end}`;
}

function createThisMonthQuery(field, row) {
    return createMonthQuery(field, 0);
}

function createLastMonthQuery(field, row) {
    return createMonthQuery(field, 1);
}

function createYearQuery(field, subtract = 0) {
    const start = moment().subtract(subtract, "years").startOf("year");
    const end = moment().subtract(subtract, "years").endOf("year");
    return `${field} >= ${+start} AND ${field} <= ${+end}`;
}

function createThisYearQuery(field, row) {
    return createYearQuery(field, 0);
}

function createLastYearQuery(field, row) {
    return createYearQuery(field, 1);
}

function createLastDaysQuery(field, row) {
    const start = moment().subtract(row.$lastDays, "days").startOf("day");
    const end = moment().endOf("day");
    return `${field} >= ${+start} AND ${field} <= ${+end}`;
}

function createLocBelowQuery(field, row) {
    return `${field} IN GET_CHILDREN('${
        row.$locBelow.object[row.$locBelow.key]
    }')`;
}

function createLocLevelQuery(field, row) {
    const id = row.$locLevel.object[row.$locLevel.key];
    return `${field} = '${id}'`;
}

function createLastCycleQuery(field, row) {
    return `(${field} >= MAX_CYCLE(AnmID) - ${(row.$lastCycle || 0) - 1})`;
}

function createConditionQuery(advancedRow, separator) {
    let query = [];
    for (let row of advancedRow) {
        for (let key in row) {
            if (keyFunctions[key]) {
                const res = keyFunctions[key](row[key]);
                if (res) query.push(res);
            } else {
                const res = createBaseQuery(key, row[key]);
                if (res) query.push(res);
            }
        }
    }
    if (query.length === 0) return "";
    if (query.length === 1) return query[0];
    return `(${query.join(separator)})`;
}

function createOrQuery(advancedRow) {
    return createConditionQuery(advancedRow, " OR ");
}

function createAndQuery(advancedRow) {
    return createConditionQuery(advancedRow, " AND ");
}

const keyFunctions = {
    $or: createOrQuery,
    $and: createAndQuery,
    $sanGte: createGreaterThanOrEqualsQuery,
    $eq: createEqualsQuery,
    $sanEq: createEqualsQuery,
    $sanLte: createLessThanOrEqualsQuery,
    $sanNe: createNotEqualsQuery,
    $includes: createStringIncludesQuery,
    $startsWith: createStartsWithQuery,
    $endsWith: createEndsWithQuery,
    $dateEq: createDateEqualsQuery,
    $dateGte: createDateGreaterThanOrEqualsQuery,
    $dateLte: createDateLessThanOrEqualsQuery,
    $techGroupAge: createTechnologyGroupAgeQuery,
    $yesterday: createYesterdayQuery,
    $thisWeek: createThisWeekQuery,
    $lastWeek: createLastWeekQuery,
    $thisMonth: createThisMonthQuery,
    $lastMonth: createLastMonthQuery,
    $thisYear: createThisYearQuery,
    $lastYear: createLastYearQuery,
    $lastDays: createLastDaysQuery,
    $locBelow: createLocBelowQuery,
    $locLevel: createLocLevelQuery,
    $lastCycle: createLastCycleQuery,
};

function createAdvancedQueryFilter(advanced) {
    const key = Object.keys(advanced)[0];
    return keyFunctions[key](advanced[key]);
}

function createCountValuesAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `COUNT(CASE WHEN key = '${parameter}'{{QUERY_DUPLICATOR}}${
        advancedQuery ? ` AND ${advancedQuery} ` : ""
    } THEN [value] END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createCountValuesDistinctAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `COUNT(DISTINCT CASE WHEN key = '${parameter}'{{QUERY_DUPLICATOR}}${
        advancedQuery ? ` AND ${advancedQuery} ` : ""
    } THEN [value] END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createCountAnimalsAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `COUNT(CASE WHEN key = '${parameter}'{{QUERY_DUPLICATOR}}${
        advancedQuery ? ` AND ${advancedQuery} ` : ""
    } THEN AnmID END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createCountAnimalsDistinctAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `COUNT(DISTINCT CASE WHEN key = '${parameter}'{{QUERY_DUPLICATOR}}${advancedQuery ? ` AND ${advancedQuery} ` : ""} THEN AnmID END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createAverageCycleAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `AVG(CASE WHEN key = '${parameter}' {{QUERY_DUPLICATOR}}${advancedQuery ? ` AND ${advancedQuery}` : ""} THEN cycle END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createMinCycleAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `MIN(CASE WHEN key = '${parameter}' {{QUERY_DUPLICATOR}}${advancedQuery ? ` AND ${advancedQuery}` : ""} THEN cycle END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

function createMaxCycleAggregator(param, advancedQuery) {
    const {parameter, id} = param;
    return `MAX(CASE WHEN key = '${parameter}' {{QUERY_DUPLICATOR}}${advancedQuery ? ` AND ${advancedQuery}` : ""} THEN cycle END) as [${id}{{PARAMETER_DUPLICATOR}}]`;
}

const typeFunctions = {
    COUNT_VALUES: createCountValuesAggregator,
    COUNT_VALUES_DISTINCT: createCountValuesDistinctAggregator,
    COUNT_ANIMAL: createCountAnimalsAggregator,
    COUNT_ANIMAL_DISTINCT: createCountAnimalsDistinctAggregator,
    AVG_CYCLE: createAverageCycleAggregator,
    MIN_CYCLE: createMinCycleAggregator,
    MAX_CYCLE: createMaxCycleAggregator,
};

function replaceTechnologyGroupQuery(query, names) {
    const queries = [];
    for (let name of names) {
        queries.push(
            query
                .replace("{{QUERY_DUPLICATOR}}", ` AND group_nr = '${name}'`)
                .replace("{{PARAMETER_DUPLICATOR}}", `#${name}`)
        );
    }
    return queries.join(", ");
}

function createDuplicateLastGroups(offset = 0) {
    return function duplicateLastTechnologyGroupsQuery(query, filterValue) {
        const names = getLastTechnologyGroupNames(filterValue.group, offset, {
            technologyGroupStart: getFirstTechnologyGroupStart(),
            technologyGroupWeeks: getTechnologyGroupWeeks(),
        });
        return replaceTechnologyGroupQuery(query, names);
    };
}

function createTechnologyGroupPickerDuplicatedQuery(query, filterValue) {
    return replaceTechnologyGroupQuery(query, filterValue.group);
}

function createGroupQueryDuplicator(query, filterValue) {
    const {group} = filterValue;
    return query
        .replace("{{QUERY_DUPLICATOR}}", ` AND group_nr = '${group}'`)
        .replace("{{PARAMETER_DUPLICATOR}}", `#${group}`);
}

function createTechnologyGroupWeekDuplicator(query, filterValue) {
    const {group} = filterValue;
    const time = moment.utc().subtract(group, "weeks");
    const technologyGroup = getTechnologyGroupByTime(
        +time,
        getFirstTechnologyGroupStart(),
        getTechnologyGroupWeeks()
    );
    const name = formatTechnoGroupName(
        technologyGroup.start,
        technologyGroup.end,
        true
    );
    return query
        .replace("{{QUERY_DUPLICATOR}}", ` AND group_nr = '${name}'`)
        .replace("{{PARAMETER_DUPLICATOR}}", `#${name}`);
}

function createCycleGroupDuplicator(query, filterValue) {
    const {value} = filterValue;
    return query
        .replace("{{QUERY_DUPLICATOR}}", ` AND cycle = ${value}`)
        .replace("{{PARAMETER_DUPLICATOR}}", `#${value}`);
}

function createCycleRangeGroupDuplicator(query, filterValue) {
    const {value} = filterValue;
    return query
        .replace(
            "{{QUERY_DUPLICATOR}}",
            ` AND cycle BETWEEN ${value[0]} AND ${value[1]}`
        )
        .replace("{{PARAMETER_DUPLICATOR}}", `#${value[0]}-${value[1]}`);
}

function createCycleGroupedDuplicator(query, filterValue) {
    const {value} = filterValue;
    return value.map((row) => {
        return query
            .replace(
                "{{QUERY_DUPLICATOR}}",
                ` AND cycle BETWEEN ${row[0]} AND ${row[1]}`
            )
            .replace("{{PARAMETER_DUPLICATOR}}", `#${row[0]}-${row[1]}`);
    });
}

function createLastGroupsQueryDuplicator(query, filterValue, queryParams) {
    if (!queryParams?.[0])
        return query
            .replace("{{QUERY_DUPLICATOR}}", "")
            .replace("{{PARAMETER_DUPLICATOR}}", "");
    return queryParams[0].map((group) => {
        return query
            .replace("{{QUERY_DUPLICATOR}}", ` AND group_nr = '${group}'`)
            .replace("{{PARAMETER_DUPLICATOR}}", `#${group}`);
    });
}

const queryDuplicators = {
    lastXClosed: createDuplicateLastGroups(20),
    lastGroups: createDuplicateLastGroups(0),
    groupPicker: createTechnologyGroupPickerDuplicatedQuery,
    finisherGroup: createGroupQueryDuplicator,
    group: createGroupQueryDuplicator,
    week: createTechnologyGroupWeekDuplicator,
    cycle: createCycleGroupDuplicator,
    cycleRange: createCycleRangeGroupDuplicator,
    cycleGrouped: createCycleGroupedDuplicator,
    lastInsertedGroups: createLastGroupsQueryDuplicator,
    lastClosedGroups: createLastGroupsQueryDuplicator,
};

function createDuplicatedQuery(query, param, filterValue, queryParameters) {
    if (
        filterValue &&
        param?.duplicateSeries &&
        queryDuplicators[filterValue.unit]
    ) {
        return queryDuplicators[filterValue.unit](
            query,
            filterValue,
            queryParameters
        );
    }
    return query
        .replace("{{QUERY_DUPLICATOR}}", "")
        .replace("{{PARAMETER_DUPLICATOR}}", "");
}

function createSingleFieldQuery(param, filterValue, queryParameters) {
    const {type, parameter, advanced, id} = param;
    if (parameter === "MATH") return "";
    const advancedQuery = createAdvancedQueryFilter(advanced);
    if (!typeFunctions[type]) {
        return createDuplicatedQuery(
            `${
                type || "SUM"
            }(CASE WHEN key = '${parameter}'{{QUERY_DUPLICATOR}}${
                advancedQuery ? ` AND ${advancedQuery} ` : ""
            } THEN [value] END) as [${id}{{PARAMETER_DUPLICATOR}}]`,
            param,
            filterValue,
            queryParameters
        );
    }
    return createDuplicatedQuery(
        typeFunctions[type](param, advancedQuery),
        param,
        filterValue,
        queryParameters
    );
}

const additionalFieldsQuery = {
    group_nr: "LAST(group_nr)",
    cycle: "LAST(cycle)",
    cycleWeek: "LAST(cycleWeek)",
    cycleDay: "LAST(cycleDay)",
    dataEv: "LAST(dataEv)",
};

function createFieldQuery(
    paramList,
    groupBy,
    options,
    filterValue,
    queryParameters
) {
    let tmp = [];
    for (let param of paramList) {
        if (param.isOptionsParameter) {
            if (param.optionType === "Fields") {
                tmp.push(
                    `${additionalFieldsQuery[param.parameter]} as [${param.id}]`
                );
            }
        } else {
            let res = createSingleFieldQuery(
                param,
                filterValue,
                queryParameters
            );
            if (res) tmp.push(res);
        }
    }
    return tmp.join(", ");
}

function createGroupNumberOptionFilter(options) {
    const {GroupAmount, GroupYear, LastGroups} = options;
    if (GroupAmount === "year") {
        const technoGroupStartTime = getFirstTechnologyGroupStart();
        const technoGroupDuration = getTechnologyGroupWeeks();
        const groups = getTechnologyGroupWeeksByYear(
            technoGroupStartTime,
            technoGroupDuration,
            GroupYear
        ).map(
            (item) => `'${formatTechnoGroupName(item.start, item.end, true)}'`
        );
        return `group_nr IN (${groups.join(", ")})`;
    }
    if (GroupAmount === "last") {
        const technoGroupStartTime = getFirstTechnologyGroupStart();
        const technoGroupDuration = getTechnologyGroupWeeks();
        const weeks = getTechnologyGroupWeeksByYear(
            technoGroupStartTime,
            technoGroupDuration,
            moment.utc().year()
        ).filter((item) => item.start.isBefore(moment.utc()));
        if (weeks.length < LastGroups) {
            const prevYearWeeks = getTechnologyGroupWeeksByYear(
                technoGroupStartTime,
                technoGroupDuration,
                moment.utc().year() - 1
            );
            weeks.unshift(
                ...prevYearWeeks.slice(
                    prevYearWeeks.length - LastGroups + weeks.length
                )
            );
        }
        const groups = weeks.map(
            (item) => `'${formatTechnoGroupName(item.start, item.end, true)}'`
        );
        return `group_nr IN (${groups.join(", ")})`;
    }
    return "";
}

const optionFilters = {
    group_nr: createGroupNumberOptionFilter,
};

function filterItemEvIDFilter(filterItem) {
    return `EvID IN (${(filterItem.EvIDs || [])
        .map((item) => `'${item}'`)
        .join(", ")})`;
}

function filterItemAnmIDFilter(filterItem) {
    console.log("AnmID filter", filterItem);
    return `AnmID IN (${(filterItem.AnmIDs || [])
        .map((item) => `'${item}'`)
        .join(", ")})`;
}

function filterItemTechnologyGroupFilter(filterItem) {
    return `group_nr IN (${(filterItem.group || [])
        .map((item) => `'${item}'`)
        .join(", ")})`;
}

function filterItemCycleFilter(filterItem) {
    return `cycle IN (${(filterItem.cycle || [])
        .map((item) => `'${item}'`)
        .join(", ")})`;
}

const filterItemFilters = {
    EvID: filterItemEvIDFilter,
    AnmID: filterItemAnmIDFilter,
    group_nr: filterItemTechnologyGroupFilter,
    cycle: filterItemCycleFilter,
};

function filterItemDataEvQuery(filterItem) {
    const end = moment(filterItem.value).endOf(filterItem.timePeriod);
    return `(dataEv >= ${filterItem.value} AND dataEv <= ${+end})`;
}

function filterItemPlcmntIDQuery(filterItem) {
    if (filterItem.plcmntLevel === "none" || filterItem.plcmntLevel === "any")
        return `PlcmntID = '${filterItem.value}'`;
    const level =
        filterItem.plcmntLevel === "chamber"
            ? Level.CHAMBER
            : filterItem.plcmntLevel === "sector"
              ? Level.SECTOR
              : Level.BUILDING;
    return `GET_LEVEL(PlcmntID, ${level}) = '${filterItem.value}'`;
}

const filterItemQuery = {
    dataEv: filterItemDataEvQuery,
    PlcmntID: filterItemPlcmntIDQuery,
};

function filterItemDefaultQuery(filterItem, options) {
    if (filterItemFilters[options.FilterBy])
        return filterItemFilters[options.FilterBy](filterItem);
    // tutaj wchodzi tylko, gdy wybrano wartosci osi X
    if (filterItemQuery[filterItem.field])
        return filterItemQuery[filterItem.field](filterItem);
    return createEqualsQuery(filterItem.field, {$sanEq: filterItem.value});
}

function createTimeQuery(filterValue, options) {
    const {time, unit} = filterValue;
    const start = moment
        .utc()
        .subtract(time + +(options.Offset || 0), unit)
        .startOf(unit);
    return `(dataEv >= ${+start} AND dataEv <= ${+moment().endOf(
        "day"
    )}) OR dataEv IS NULL`;
}

function createRelativeQuery(filterValue, options) {
    const {time} = filterValue;
    if (time === "TODAY") {
        const date = moment().subtract(+(options.Offset || 0), "days");
        return `(dataEv >= ${+date.startOf("day")} AND dataEv <= ${+date.endOf(
            "day"
        )}) OR dataEv IS NULL`;
    }
    if (time === "YESTERDAY") {
        const date = moment().subtract(1 + +(options.Offset || 0), "days");
        const start = +date.startOf("day");
        const end = +date.endOf("day");
        return `(dataEv >= ${start} AND dataEv <= ${end}) OR dataEv IS NULL`;
    }
}

function createAbsoluteQuery(filterValue, options) {
    const {time} = filterValue;
    let start = moment(time.start);
    let end = moment(time.end);
    if (options.Offset && options.Offset > 0) {
        start = start.subtract(+options.Offset, "weeks");
        end = end.subtract(+options.Offset, "weeks");
    }
    return `(dataEv >= ${+start} AND dataEv <= ${+end}) OR dataEv IS NULL`;
}

function createGroupQuery(filterValue, options) {
    const {group} = filterValue;
    if (options.Offset && options.Offset > 0) {
        const technologyGroupStart = getFirstTechnologyGroupStart();
        const technologyGroupWeeks = getTechnologyGroupWeeks();
        const startTime = getTechnologyGroupStartByName(group, {
            technologyGroupStart,
        });
        const offsetTime = moment
            .utc(startTime * technologyGroupWeeks)
            .subtract(options.Offset, "weeks");
        const newGroup = getTechnologyGroupByTime(
            +offsetTime,
            technologyGroupStart,
            technologyGroupWeeks
        );
        return `group_nr = '${formatTechnoGroupName(
            newGroup.start,
            newGroup.end,
            true
        )}'`;
    }
    return `group_nr = '${group}'`;
}

function createTechnologyGroupWeekQuery(filterValue, options) {
    const {group: week} = filterValue;
    console.log("createTechnologyGroupWeekQuery", week, options);
    const technologyGroup = getTechnologyGroupByTime(
        moment.utc().subtract(week + (+options.Offset || 0), "weeks"),
        getFirstTechnologyGroupStart(),
        getTechnologyGroupWeeks()
    );
    const name = formatTechnoGroupName(
        technologyGroup.start,
        technologyGroup.end,
        true
    );
    return `group_nr = '${name}'`;
}

function createLastGroupsQuery(filterValue, offset = 0) {
    console.log("createLastGroupsQuery", filterValue, offset);
    const names = getLastTechnologyGroupNames(filterValue.group, offset, {
        technologyGroupStart: getFirstTechnologyGroupStart(),
        technologyGroupWeeks: getTechnologyGroupWeeks(),
    });
    return `group_nr IN (${names.map((item) => `'${item}'`).join(", ")})`;
}

function createLastXClosedQuery(filterValue, options) {
    return createLastGroupsQuery(filterValue, 20 + +(options.Offset || 0));
}

function createLastXGroupsQuery(filterValue, options) {
    return createLastGroupsQuery(filterValue, +(options.Offset || 0));
}

function createTechnologyGroupPickerQuery(filterValue, options) {
    const {group} = filterValue;
    if (options.Offset && options.Offset > 0) {
        const technologyGroupStart = getFirstTechnologyGroupStart();
        const technologyGroupWeeks = getTechnologyGroupWeeks();
        const tmp = group.map((item) => {
            const startTime = getTechnologyGroupStartByName(item, {
                technologyGroupStart,
            });
            const offsetTime = moment
                .utc(startTime * technologyGroupWeeks)
                .subtract(options.Offset, "weeks");
            const newGroup = getTechnologyGroupByTime(
                +offsetTime,
                technologyGroupStart,
                technologyGroupWeeks
            );
            return newGroup;
        });
        return `group_nr IN (${tmp
            .map((item) => {
                const name = formatTechnoGroupName(item.start, item.end, true);
                return `'${name}'`;
            })
            .join(", ")})`;
    }
    return `group_nr IN (${group.map((item) => `'${item}'`).join(", ")})`;
}

function createFinisherGroupBasicQuery(filterValue) {
    const {group} = filterValue;
    return `group_nr = '${group}'`;
}

function createCyclePickerQuery(filterValue) {
    const {value} = filterValue;
    return `cycle = ${value}`;
}

function createCycleRangePickerQuery(filterValue) {
    const {value} = filterValue;
    return `cycle >= ${value[0]} AND cycle <= ${value[1]}`;
}

function createCycleGroupPickerQuery(filterValue) {
    const {value} = filterValue;
    const cycles = new Set();
    for (let row of value) {
        for (let i = row[0]; i <= row[1]; i++) {
            cycles.add(i);
        }
    }
    return `cycle IN (${[...cycles.values()].join(", ")})`;
}

function createLastPigletPorkerGroupsQuery(
    filterValue,
    options,
    queryParameters
) {
    if (!queryParameters?.[0]) return `group_nr IN ('')`;
    return `group_nr IN (${queryParameters[0].map((group) => `'${group}'`).join(", ")})`;
}

function createAnimalKindQuery(filterValue) {
    const {value} = filterValue;
    return `AnimalKind IN (${value.join(", ")})`;
}

const filterValueQuery = {
    relative: createRelativeQuery,
    absolute: createAbsoluteQuery,
    group: createGroupQuery,
    week: createTechnologyGroupWeekQuery,
    lastXClosed: createLastXClosedQuery,
    lastGroups: createLastXGroupsQuery,
    groupPicker: createTechnologyGroupPickerQuery,
    finisherGroup: createFinisherGroupBasicQuery,
    cycle: createCyclePickerQuery,
    cycleRange: createCycleRangePickerQuery,
    cycleGrouped: createCycleGroupPickerQuery,
    lastInsertedGroups: createLastPigletPorkerGroupsQuery,
    lastClosedGroups: createLastPigletPorkerGroupsQuery,
    animalKind: createAnimalKindQuery,
};

function createFilterValueQuery(filterValue, options, queryParameters) {
    if (!filterValueQuery[filterValue.unit]) {
        return createTimeQuery(filterValue, options);
    } else {
        return filterValueQuery[filterValue.unit](
            filterValue,
            options,
            queryParameters
        );
    }
}

function createWhereQuery(
    paramList,
    groupBy,
    filter,
    options,
    filterItem,
    filterValue,
    queryParameters
) {
    let parameterSet = new Set();
    for (let {parameter, isOptionsParameter, optionType} of paramList) {
        if (parameter !== "MATH") {
            if (isOptionsParameter) {
                if (optionType === "Fields") {
                    parameterSet.add(parameter);
                }
            } else {
                parameterSet.add(parameter);
            }
        }
    }
    let query = `key in (${[...parameterSet.values()]
        .map((parameter) => `'${parameter}'`)
        .join(", ")})`;
    const optionsQuery = optionFilters[groupBy]?.(options);
    if (optionsQuery) {
        query = `(${query}) AND (${optionsQuery})`;
    }
    if (filterValue && filterItem?.field !== "dataEv") {
        const tmp = createFilterValueQuery(
            filterValue,
            options,
            queryParameters
        );
        query = `(${query}) AND (${tmp})`;
    }
    if (filterItem && options.FilterBy !== "disabled") {
        const tmp = [];
        for (let row of filterItem) {
            const itemQuery = filterItemDefaultQuery(row, options);
            if (itemQuery) {
                tmp.push(`(${itemQuery})`);
                // query = `(${query}) AND (${itemQuery})`;
            }
        }
        if (tmp.length > 0) {
            query = `(${query}) AND (${tmp.join(" AND ")})`;
        }
    }
    const filterQuery = createAdvancedQueryFilter(filter);
    if (!filterQuery) return query;
    return `(${query}) AND ${filterQuery}`;
}

function createDataEvGroupBy(options) {
    const {TimePeriod} = options;
    if (TimePeriod === "day") {
        const dayPeriod = 1000 * 60 * 60 * 24;
        return `dataEv - (dataEv % ${dayPeriod})`;
    }
    if (TimePeriod === "week") {
        return "CAST(DATE_ADD_UTC('week', DATE_DIFF_UTC('week', 345600000, dataEv, 'isoWeek'), 345600000) as number)"; // pierwszy poniedzialek
    }
    if (TimePeriod === "month") {
        return "CAST(DATE_ADD_UTC('month', DATE_DIFF_UTC('month', 0, dataEv), 0) as number)";
    }
    if (TimePeriod === "year") {
        return "CAST(DATE_ADD_UTC('year', DATE_DIFF_UTC('year', 0, dataEv), 0) as number)";
    }
    return "dataEv";
}

function createPlcmntIDGroupBy(options) {
    const {PlcmntLevel} = options;
    switch (PlcmntLevel) {
        case "chamber":
            return `GET_LEVEL(PlcmntID, ${Level.CHAMBER})`;
        case "sector":
            return `GET_LEVEL(PlcmntID, ${Level.SECTOR})`;
        case "building":
            return `GET_LEVEL(PlcmntID, ${Level.BUILDING})`;
        default:
            return "PlcmntID";
    }
}

function createValueGroupBy(options) {
    const value = +options.ValueGroup;
    if (value > 0 && !isNaN(value))
        return `ROUND([value] / ${value}) * ${value}`;
    return "[value]";
}

const groupByFunctions = {
    dataEv: createDataEvGroupBy,
    PlcmntID: createPlcmntIDGroupBy,
    "[value]": createValueGroupBy,
};

function createGroupByQuery(groupBy, options) {
    if (!groupByFunctions[groupBy]) return groupBy;
    return groupByFunctions[groupBy](options);
}

function sortByValue(options, paramList) {
    const paramString = paramList.map(
        (parameter) => `[${parameter.id}] ${options.SortType}`
    );
    return `${paramString.join(", ")}`;
}

const sortFieldFunctions = {
    value: sortByValue,
};

function sortByGroupNumber(options, paramList) {
    return `group_nr_number ${options.SortType}`;
}

const sortFieldNameFunction = {
    group_nr: sortByGroupNumber,
};

function createSortQuery(options, paramList, groupBy) {
    if (!sortFieldFunctions[options.SortColumn]) {
        if (sortFieldNameFunction[groupBy])
            return sortFieldNameFunction[groupBy](options, paramList);
        return `${options.SortColumn} ${options.SortType}`;
    }
    return sortFieldFunctions[options.SortColumn](options, paramList);
}

function createRelativeTimeMissingData() {
    return null; // tak ma byc, dla tych query nie uzupelniamy
}

function getStartEndFilterValue(filterValue, options) {
    const {time, unit} = filterValue;
    if (unit === "absolute") {
        const {start, end} = time;
        const adjustedStart = moment.utc(
            moment(start)
                .startOf(options.TimePeriod)
                .format(moment.HTML5_FMT.DATE)
        );
        const adjustedEnd = moment.utc(end);
        return {start: adjustedStart, end: adjustedEnd};
    }
    const start = moment
        .utc()
        .subtract(time, unit)
        .startOf(options.TimePeriod === "week" ? "week" : unit);
    return {start, end: moment.utc().startOf("day")};
}

function createAbsoluteTimeMissingData(options, filterValue) {
    const times = [];
    const {start, end} = getStartEndFilterValue(filterValue, options);
    const diff = end.diff(start, options.TimePeriod) + 1;
    for (let i = 0; i < diff; i++) {
        const date = start.clone().add(i, options.TimePeriod);
        times.push(`{name: ${+date}}`);
    }
    return `WITH missing as (SELECT * FROM @[${times.join(", ")}])`;
}

const missingDataEvFilterValueFunctions = {
    relative: createRelativeTimeMissingData,
    absolute: createAbsoluteTimeMissingData,
};

function createMissingDaysFunction(options, filterValue) {
    if (!missingDataEvFilterValueFunctions[filterValue.unit]) {
        const times = [];
        const {start} = getStartEndFilterValue(filterValue, options);
        const diff = moment.utc().diff(start, options.TimePeriod) + 1;
        for (let i = 0; i < diff; i++) {
            const date = start.clone().add(i, options.TimePeriod);
            times.push(`{name: ${+date}}`);
        }
        return `WITH missing as (SELECT * FROM @[${times.join(", ")}])`;
    }
    return missingDataEvFilterValueFunctions[filterValue.unit](
        options,
        filterValue
    );
}

function createDataEvMissingDataTimePicker(
    query,
    options,
    filterValue,
    sortQuery
) {
    if (options.TimePeriod === "none") return null;
    const withQuery = createMissingDaysFunction(options, filterValue);
    if (!withQuery) return null;
    return `
        ${withQuery},
        dataCte AS (SELECT * FROM (SELECT * FROM missing) as s1 LEFT JOIN (${query}) as s2 ON s1.name = s2.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

const missingDataEvPickerType = {
    time: createDataEvMissingDataTimePicker,
};

function createDataEvMissingDataOtherPickers(
    query,
    options,
    filterValue,
    sortQuery
) {
    if (options.TimePeriod === "none") return null;
    console.log(options.TimePeriod);
    return `
        WITH
            baseCte AS (${query}),
            minMaxCte AS (SELECT MIN(name) as minDate, MAX(name) as maxDate FROM baseCte),
            missingCte AS (SELECT GENERATE_DATES(minDate, maxDate, '${options.TimePeriod}') as dates FROM minMaxCte),
            missingCteSimplified AS (SELECT VALUE ARRAY(dates) -> 0 FROM missingCte),
            dataCte AS (SELECT *, missingCteSimplified.date as name FROM missingCteSimplified LEFT JOIN baseCte ON missingCteSimplified.date = baseCte.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

function createDataEvMissingData(options, filterValue, query, sortQuery) {
    if (!missingDataEvPickerType[filterValue.pickerType])
        return createDataEvMissingDataOtherPickers(
            options,
            filterValue,
            query,
            sortQuery
        );
    return missingDataEvPickerType[filterValue.pickerType](
        options,
        filterValue,
        query,
        sortQuery
    );
}

function createCycleMissingQuery(query, options, filterValue, sortQuery) {
    return `
        WITH base AS (${query}),
        maxCte AS (SELECT MAX(name) as maxName FROM base),         
        missing AS (SELECT VALUE GENERATE_ARRAY(CONST('FIRST_CYCLE_INDEX'), maxCte.maxName) FROM maxCte),
        dataCte AS (SELECT * FROM missing LEFT JOIN base ON missing.name = base.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

function createCycleWeekOrDayMissingQuery(
    query,
    options,
    filterValue,
    sortQuery
) {
    return `
        WITH base AS (${query}),
        minCte AS (SELECT CASE WHEN MIN(name) > 0 THEN 0 ELSE MIN(name) END as minName FROM base),
        maxCte AS (SELECT MAX(name) as maxName FROM base),
        missing AS (SELECT VALUE GENERATE_ARRAY(minCte.minName, maxCte.maxName) FROM maxCte FULL OUTER JOIN minCte),
        dataCte AS (SELECT * FROM missing LEFT JOIN base ON missing.name = base.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

function createGroupNrMissingQuery(query, options, filterValue, sortQuery) {
    const {start, end} = getStartEndFilterValue(filterValue, options);
    const years = [];
    let tmp = start.clone();
    do {
        years.push(tmp.year());
        tmp.add(1, "year");
    } while (tmp.isSameOrBefore(end));
    const technologyGroupStart = getFirstTechnologyGroupStart();
    const technologyGroupWeeks = getTechnologyGroupWeeks();
    const groups = flatten(
        years.map((year) =>
            getTechnologyGroupWeeksByYear(
                technologyGroupStart,
                technologyGroupWeeks,
                year
            )
        )
    );
    const groupsString = groups.map(
        (group) =>
            `{name: '${formatTechnoGroupName(group.start, group.end, true)}'}`
    );
    return `
        WITH groupsCte AS (SELECT * FROM @[${groupsString.join(", ")}]),
        dataCte AS (SELECT * FROM groupsCte LEFT JOIN (${query}) as s2 ON groupsCte.name = s2.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

function createDayMissingQuery(query, options, filterValue, sortQuery) {
    const days = [1, 2, 3, 4, 5, 6, 7];
    return `
        WITH daysCte AS (SELECT * FROM @[${days
            .map((day) => `{name: ${day}}`)
            .join(", ")}]),
        dataCte AS (SELECT * FROM daysCte LEFT JOIN (${query}) as s2 ON daysCte.name = s2.name)
        SELECT * FROM dataCte ORDER BY ${sortQuery}
    `;
}

const missingDataFunctions = {
    dataEv: createDataEvMissingData,
    cycle: createCycleMissingQuery,
    cycleWeek: createCycleWeekOrDayMissingQuery,
    cycleDay: createCycleWeekOrDayMissingQuery,
    group_nr: createGroupNrMissingQuery,
    day: createDayMissingQuery,
};

function createMissingDataQuery(
    query,
    groupBy,
    options,
    filterValue,
    sortQuery,
    limit
) {
    const limitQuery = limit !== undefined ? `LIMIT ${limit}` : "";
    if (!options.InsertDomain)
        return `${query} ORDER BY ${sortQuery} ${limitQuery}`;
    return (
        missingDataFunctions[groupBy]?.(
            query,
            options,
            filterValue,
            sortQuery
        ) || `${query} ORDER BY ${sortQuery} ${limitQuery}`
    );
}

function createQueryWithoutMath(
    fieldQuery,
    groupByQuery,
    whereQuery,
    additionalFieldsQuery
) {
    let additionalValues = "";
    if (groupByQuery === "group_nr")
        additionalValues +=
            ", SORTABLE_TECH_GROUP(group_nr) as group_nr_number";
    if (!fieldQuery)
        return `SELECT ${groupByQuery} as name, ${additionalFieldsQuery}${additionalValues} FROM ParamsTable WHERE ${whereQuery} GROUP BY ${groupByQuery}`;
    return `SELECT ${groupByQuery} as name, ${additionalFieldsQuery}, ${fieldQuery}${additionalValues} FROM ParamsTable WHERE ${whereQuery} GROUP BY ${groupByQuery}`;
}

function addDefaultToMathQuery(math) {
    let copy = math;
    const regex = new RegExp("\\[[^\\]]+\\]", "g");
    let group;
    while ((group = regex.exec(math)) !== null) {
        copy = copy.replace(group[0], `DEF(${group[0]}, 0)`);
    }
    return copy;
}

function createMathSingleQuery(parameter) {
    const {id, math} = parameter;
    if (!math) return "";
    const newMath = addDefaultToMathQuery(math);
    return `(${newMath}) as [${id}]`;
}

function createMathQuery(mathParameters) {
    let tmp = [];
    for (let parameter of mathParameters) {
        let res = createMathSingleQuery(parameter);
        if (res) tmp.push(res);
    }
    return tmp.join(", ");
}

function createMathParametersQuery(mathParameters, query) {
    const mathQuery = createMathQuery(mathParameters);
    if (mathQuery === "") return query;
    return `
        SELECT
            *, ${mathQuery}
        FROM
            (${query})
    `;
}

function createQuery(
    fieldQuery,
    groupByQuery,
    whereQuery,
    mathParameters,
    additionalFieldsQuery
) {
    let query = createQueryWithoutMath(
        fieldQuery,
        groupByQuery,
        whereQuery,
        additionalFieldsQuery
    );
    if (mathParameters.length > 0) {
        return createMathParametersQuery(mathParameters, query);
    }
    return query;
}

function getMathParameters(paramList) {
    return paramList.filter(({parameter}) => parameter === "MATH");
}

function createAdditionalFieldQuery(paramList, filterValue) {
    const tmp = [];
    for (let {
        advanced,
        parameter,
        isOptionsParameter,
        optionType,
    } of paramList) {
        if (!isOptionsParameter || optionType === "Field") {
            const query = createAdvancedQueryFilter(advanced);
            tmp.push(
                `CASE WHEN key = '${parameter}'${
                    query ? ` AND ${query} ` : ""
                } THEN {{type}} ELSE`
            );
        }
    }
    let caseQuery = `${tmp.join(" ")} NULL ${new Array(tmp.length)
        .fill("END")
        .join(" ")}`;
    return `ARRAY(DISTINCT ${caseQuery.replaceAll(
        "{{type}}",
        "EvID"
    )}) as EvIDs, ARRAY(DISTINCT ${caseQuery.replaceAll(
        "{{type}}",
        "AnmID"
    )}) as AnmIDs, ARRAY(DISTINCT ${caseQuery.replaceAll(
        "{{type}}",
        "group_nr"
    )}) as groups, ARRAY(DISTINCT ${caseQuery.replaceAll(
        "{{type}}",
        "cycle"
    )}) as cycles`;
}

const animalOptions = {
    location:
        "LAST_NOT_NULL(CASE WHEN key = 'currentLocation' THEN [value] END)",
    status: "LAST_NOT_NULL(CASE WHEN key = 'sowState' THEN [value] END)",
    statusDays:
        "LAST_NOT_NULL(CASE WHEN key = 'sowStateDays' THEN [value] END)",
    sowIndex: "LAST_NOT_NULL(CASE WHEN key = 'index' THEN [value] END)",
    idleDays: "LAST_NOT_NULL(CASE WHEN key = 'idleDays' THEN [value] END)",
    tattoo: "FIRST_NOT_NULL(CASE WHEN key = 'tattooNumber' THEN [value] END)",
    motherNumber: "FIRST_NOT_NULL(CASE WHEN key = 'mother' THEN [value] END)",
    fatherNumber: "FIRST_NOT_NULL(CASE WHEN key = 'father' THEN [value] END)",
    motherTattoo:
        "FIRST_NOT_NULL(CASE WHEN key = 'motherTattoo' THEN [value] END)",
    fatherTattoo:
        "FIRST_NOT_NULL(CASE WHEN key = 'fatherTattoo' THEN [value] END)",
    motherRace: "FIRST_NOT_NULL(CASE WHEN key = 'motherRace' THEN [value] END)",
    fatherRace: "FIRST_NOT_NULL(CASE WHEN key = 'motherRace' THEN [value] END)",
    age: "FIRST_NOT_NULL(CASE WHEN key = 'age' THEN [value] END)",
    rfid: "FIRST_NOT_NULL(RFID)",
    herdNumber: "FIRST_NOT_NULL(CASE WHEN key = 'herd' THEN [value] END)",
    supplier: "FIRST_NOT_NULL(CASE WHEN key = 'supplier' THEN [value] END)",
    insertionTime:
        "FIRST_NOT_NULL(CASE WHEN key = 'insertionTime' THEN [value] END)",
    race: "FIRST_NOT_NULL(CASE WHEN key = 'race' THEN [value] END)",
};

const animalOptionsKeys = {
    location: "currentLocation",
    status: "sowState",
    statusDays: "sowStateDays",
    sowIndex: "index",
    idleDays: "idleDays",
    tattoo: "tattooNumber",
    motherNumber: "mother",
    fatherNumber: "father",
    motherTattoo: "motherTattoo",
    fatherTattoo: "fatherTattoo",
    motherRace: "motherRace",
    fatherRace: "fatherRace",
    age: "age",
    herdNumber: "herd",
    supplier: "supplier",
    insertionTime: "insertionTime",
    race: "race",
};

function createAdditionalValuesQuery(query, options, paramList) {
    if (!Object.keys(options.Animal || {}).some((key) => options.Animal[key]))
        return query;
    let filter = [],
        keys = [];
    for (let param of paramList) {
        if (param.isOptionsParameter && param.optionType === "Animal") {
            filter.push(`${animalOptions[param.parameter]} as [${param.id}]`);
            keys.push(`'${animalOptionsKeys[param.parameter]}'`);
        }
    }
    if (filter.length === 0) return query;
    return `
        WITH
            bCte AS (${query}),
            additionalCte AS (SELECT AnmID as id, ${filter.join(
                ", "
            )} FROM ParamsTable WHERE key IN (${keys.join(
                ", "
            )}) GROUP BY AnmID),
            allCte AS (SELECT * FROM bCte LEFT JOIN additionalCte ON bCte.name = additionalCte.id)
        SELECT * FROM allCte
    `;
}

export function createQueryForCustomComponent(
    paramList,
    groupBy,
    filter,
    options,
    filterItem,
    filterValue,
    queryParameters,
    limit = undefined
) {
    const fieldQuery = createFieldQuery(
        paramList,
        groupBy,
        options,
        filterValue,
        queryParameters
    );
    const whereQuery = createWhereQuery(
        paramList,
        groupBy,
        filter,
        options,
        filterItem,
        filterValue,
        queryParameters
    );
    const groupByQuery = createGroupByQuery(groupBy, options);
    const sortQuery = createSortQuery(options, paramList, groupBy);
    const mathParameters = getMathParameters(paramList);
    const additionalFieldsQuery = createAdditionalFieldQuery(
        paramList,
        filterValue
    );
    let query = createQuery(
        fieldQuery,
        groupByQuery,
        whereQuery,
        mathParameters,
        additionalFieldsQuery
    );
    let additionalValuesQuery = createAdditionalValuesQuery(
        query,
        options,
        paramList
    );
    return createMissingDataQuery(
        additionalValuesQuery,
        groupBy,
        options,
        filterValue,
        sortQuery,
        limit
    );
}

export function createQueryForGaugeComponent(
    paramList,
    groupBy,
    filter,
    options,
    filterItem,
    filterValue
) {
    const fieldQuery = createFieldQuery(paramList, groupBy, options);
    const mathParameters = getMathParameters(paramList);
    const whereQuery = createWhereQuery(
        paramList,
        groupBy,
        filter,
        options,
        filterItem,
        filterValue
    );
    const query = `SELECT ${fieldQuery} FROM ParamsTable WHERE ${whereQuery}`;
    if (mathParameters.length > 0) {
        return createMathParametersQuery(mathParameters, query);
    }
    return query;
}

const detailsFieldQuery = {
    group_nr: "FIRST_NOT_NULL(group_nr) as group_nr",
    cycle: "FIRST_NOT_NULL(cycle) as cycle",
    inseminationDate:
        "ARRAY_NOT_NULL(CASE WHEN key = 'boar' THEN dataEv END) as inseminationDate",
    inseminationBoar:
        "ARRAY_NOT_NULL(CASE WHEN key = 'boar' THEN [value] END) as inseminationBoar",
    usgDate:
        "ARRAY_NOT_NULL(CASE WHEN key = 'usgResult' THEN dataEv END) as usgDate",
    usgResult:
        "ARRAY_NOT_NULL(CASE WHEN key = 'usgResult' THEN [value] END) as usgResult",
    bornAlive:
        "FIRST_NOT_NULL(CASE WHEN key = 'liveBornPiglet' THEN [value] END) as bornAlive",
    bornDead:
        "FIRST_NOT_NULL(CASE WHEN key = 'deadBornPiglet' THEN [value] END) as bornDead",
    bornMummy:
        "FIRST_NOT_NULL(CASE WHEN key = 'mummyBornPiglet' THEN [value] END) as bornMummy",
    bornWeak:
        "FIRST_NOT_NULL(CASE WHEN key = 'weakBornPiglet' THEN [value] END) as bornWeak",
    bornGilt:
        "FIRST_NOT_NULL(CASE WHEN key = 'giltBornPiglet' THEN [value] END) as bornGilt",
    fallPiglets:
        "SUM(CASE WHEN key = 'deadPiglet' THEN [value] END) as fallPiglets",
    fallPigletsAge:
        "AVG(CASE WHEN key = 'deadPigletAge' THEN [value] END) as fallPigletsAge",
    fostering: "SUM(CASE WHEN key = 'fostering' THEN [value] END) as fostering",
    weighting:
        "FIRST_NOT_NULL(CASE WHEN key = 'bornPigletWeight' THEN [value] END) as weighting",
    separation:
        "SUM(CASE WHEN key = 'weanedPiglet' THEN [value] END) as separation",
    activeNipples:
        "FIRST_NOT_NULL(CASE WHEN key = 'activeNipples' THEN [value] END) as activeNipples",
    noPregnancy: `FIRST_NOT_NULL(CASE WHEN key = 'technologyGroupRemoval' AND [value] = ${REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PREGNANCY} THEN dataEv END) as noPregnancy`,
    plcmntID:
        "GET(FIND_ANIMAL_PARAM('currentLocation', 'last any', dataEv, AnmID, '*', '*'), 'value') as plcmntID",
    race: "FIRST_NOT_NULL(race) as race",
    raceCode: "FIRST_NOT_NULL(race) as raceCode",
    AnmID: "IIF(AnmID IS NOT NULL, AnmID, group_nr) as AnmID",
    idleDays:
        "LAST_NOT_NULL(CASE WHEN key = 'idleDays' THEN [value] END) as idleDays",
};

function createDetailCustomQuery(detail, grouped, name, field) {
    const param = grouped[detail.replace(`_${name}`, "")];
    if (param) {
        let res = createSingleFieldQuery({
            ...param,
            type: "FIRST_NOT_NULL",
            id: `${param.id}_${name}`,
        });
        if (res) {
            return res.replace("THEN [value]", `THEN ${field}`);
        }
    }
    return null;
}

function createDetailDateCustomQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Date", "dataEv");
}

function createDetailedDictionaryCustomQuery(detail, grouped) {
    return createDetailCustomQuery(
        detail,
        grouped,
        "Dictionary",
        "DictionaryID"
    );
}

function createDetailedPlcmntIDCustomQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "PlcmntID", "PlcmntID");
}

function createDetailedGroupDayQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "GroupDay", "groupDay");
}

function createDetailedDayQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Day", "day");
}

function createDetailedMonthQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Month", "month");
}

function createDetailedWeekQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Week", "week");
}

function createDetailedYearQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Year", "year");
}

function createDetailedCycleDayQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "CycleDay", "cycleDay");
}

function createDetailedCycleWeekQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "CycleWeek", "cycleWeek");
}

function createDetailedCycleQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "Cycle", "cycle");
}

function createDetailedUserQuery(detail, grouped) {
    return createDetailCustomQuery(detail, grouped, "User", "user");
}

const detailsCustomFunctions = {
    Date: createDetailDateCustomQuery,
    Dictionary: createDetailedDictionaryCustomQuery,
    PlcmntID: createDetailedPlcmntIDCustomQuery,
    GroupDay: createDetailedGroupDayQuery,
    Day: createDetailedDayQuery,
    Month: createDetailedMonthQuery,
    Week: createDetailedWeekQuery,
    Year: createDetailedYearQuery,
    CycleDay: createDetailedCycleDayQuery,
    CycleWeek: createDetailedCycleWeekQuery,
    Cycle: createDetailedCycleQuery,
    User: createDetailedUserQuery,
};

function createDetailsFieldQuery(details, paramList) {
    const dictionary = createKeyToIndexDictionary(paramList, "id");
    const tmp = [];
    for (let detail of details) {
        if (!dictionary.hasOwnProperty(detail)) {
            if (detailsFieldQuery[detail]) {
                tmp.push(detailsFieldQuery[detail]);
            } else {
                const split = detail.split("_");
                const lastToken = split.pop();
                if (!Object.keys(detailsCustomFunctions).includes(lastToken)) {
                    tmp.push(detail);
                }
            }
        }
    }
    return tmp.join(", ");
}

function createFieldQueryForAdvancedMode(paramList, groupBy, options) {
    let tmp = [];
    for (let param of paramList) {
        if (Object.keys(typeFunctions).includes(param.type)) {
            let res = createSingleFieldQuery({
                ...param,
                type: "FIRST_NOT_NULL",
            });
            if (res) tmp.push(res);
        } else {
            let res = createSingleFieldQuery(param);
            if (res) tmp.push(res);
        }
    }
    let query = tmp.join(", ");
    for (let key in options.Fields || {}) {
        if (options.Fields[key] && groupBy !== key) {
            query +=
                query.length === 0
                    ? `${additionalFieldsQuery[key]} as [${key}]`
                    : `, ${additionalFieldsQuery[key]} as [${key}]`;
        }
    }
    return query;
}

function createAdvancedFieldQuery(paramList, group, options, details) {
    const query = createFieldQueryForAdvancedMode(paramList, group, options);
    const grouped = createKeyToValueDictionary(paramList, "id");
    const dataFields = [];
    for (let detail of details) {
        const split = detail.split("_");
        const lastToken = split.pop();
        if (detailsCustomFunctions[lastToken]) {
            const res = detailsCustomFunctions[lastToken](detail, grouped);
            if (res) dataFields.push(res);
        }
    }
    if (dataFields.length > 0) {
        return `${query}, ${dataFields}`;
    }
    return query;
}

function createDetailsQuery(dataFieldQuery, fieldQuery, whereQuery) {
    const id =
        "IIF(FIRST(AnmID) IS NOT NULL, CONCAT(FIRST(AnmID), '_', cycleIndex), FIRST(group_nr)) as id";
    // const id = "CONCAT(FIRST(AnmID), '_', FIRST(cycleIndex)) as id";
    return `
        WITH 
            dataCte AS (SELECT ${id}, ${dataFieldQuery} FROM ParamsTable GROUP BY IIF(AnmID IS NOT NULL, AnmID, group_nr), cycleIndex),
            itemCte AS (SELECT ${id}, ${fieldQuery} FROM ParamsTable WHERE ${whereQuery} GROUP BY IIF(AnmID IS NOT NULL, AnmID, group_nr), cycleIndex)
        SELECT * FROM dataCte RIGHT JOIN itemCte ON dataCte.id = itemCte.id
    `;
}

export function createQueryForCustomComponentDetails(
    paramList,
    groupBy,
    filter,
    options,
    filterItem,
    filterValue,
    details
) {
    const dataFieldQuery = createDetailsFieldQuery(details, paramList);
    const fieldQuery = createAdvancedFieldQuery(
        paramList,
        groupBy,
        options,
        details
    );
    const whereQuery = createWhereQuery(
        paramList,
        groupBy,
        filter,
        options,
        filterItem,
        filterValue
    );
    return createDetailsQuery(dataFieldQuery, fieldQuery, whereQuery);
}

function createCountDistinctSummaryQuery(row, index) {
    return `COUNT(DISTINCT [${row.field}]) as [${row.field}_${index}]`;
}

const summaryQuery = {
    COUNT_DISTINCT: createCountDistinctSummaryQuery,
};

function createSummaryQuery(summary) {
    return summary
        .map((row, index) => {
            if (summaryQuery[row.summary])
                return summaryQuery[row.summary](row, index);
            return `${row.summary}([${row.field}]) as [${row.field}_${index}]`;
        })
        .join(", ");
}

export function createQueryForSummary(summary) {
    const query = createSummaryQuery(summary);
    return `SELECT ${query} FROM ?`;
}

export function createMathSummaryQuery(parameter) {
    const regex = new RegExp("\\[\\S*\\]", "g");
    const fields = parameter.math.match(regex);
    let tmp = parameter.math;
    for (let field of fields || []) {
        tmp = tmp.replaceAll(field, `SUM(${field})`);
    }
    return `SELECT VALUE ${tmp} FROM ?`;
}
